import { Option, Schema as S } from "effect";
import * as AsyncData from "foldkit/asyncData";

import { apiData } from "../api/asyncData";
import { FeaturesConfig } from "../api/schemas/account";
import { AttributeSchemaList } from "../api/schemas/attributeSchema";
import { EventActivity } from "../api/schemas/events";
import { LedgerBalance } from "../api/schemas/ledger";
import { TagList } from "../api/schemas/tags";
import { Auth } from "../auth/model";
import { PageModel, type PageTag } from "../pages";
import type { Shared } from "../pages/shared";
import * as LogInteraction from "../widgets/logInteraction";
import * as Sidebar from "../widgets/sidebar";
import { Route, asOfOf } from "./routes";
import { Theme } from "./theme";

export const Model = S.Struct({
	auth: Auth,
	logInteraction: LogInteraction.Model,
	/** The shell's left sidebar (U16). */
	sidebar: Sidebar.Model,
	/** Bumped on every ClerkReady so the session subscription restarts even when a
	 * dev-server-restored Model already carries `clerkReady: true`. */
	clerkEpoch: S.Number,
	/** Bumped on sign-out; result Messages tagged with an older generation are discarded (KTD3). */
	sessionGeneration: S.Number,
	clerkReady: S.Boolean,
	route: Route,
	page: S.Option(PageModel),
	balance: apiData(LedgerBalance).schema,
	features: apiData(FeaturesConfig).schema,
	tags: apiData(TagList).schema,
	attributeSchema: apiData(AttributeSchemaList).schema,
	/** The month the time-travel picker shows (`YYYY-MM`; "" follows asOf or today) and its heatmap. */
	activityMonth: S.String,
	activity: apiData(EventActivity).schema,
	timeTravelOpen: S.Boolean,
	integrationsEnabled: S.Boolean,
	billingPreviewEnabled: S.Boolean,
	/** The PostHog `a2a-context` flag; the effective gate is `a2aContextEnabled`. */
	a2aContextFlag: S.Boolean,
	/** A record or entry write hit the plan limit; the shell offers Billing while the flag is on. */
	limitNoticeOpen: S.Boolean,
	/** The first-run import offer has been made or dismissed (R20, KTD7). */
	importDismissed: S.Boolean,
	theme: Theme,
	translationsReady: S.Boolean,
});
export type Model = typeof Model.Type;

export const isAdmin = (model: Model): boolean =>
	model.auth._tag === "SignedIn" && model.auth.user.isAdmin;

/** The product owners: the only people the Admin page exists for (server `core/owners.ts` enforces it). */
export const OWNER_EMAILS: ReadonlySet<string> = new Set([
	"sam.miller@rhysomn.com",
	"kamn.miller@gmail.com",
]);

export const isOwner = (model: Model): boolean =>
	model.auth._tag === "SignedIn" &&
	OWNER_EMAILS.has(model.auth.user.email.trim().toLowerCase());

/** Tasks, Knowledge and Agents exist only while PostHog AND the server (`features.a2aContextEnabled`) both say so. */
export const a2aContextEnabled = (
	model: Pick<Model, "a2aContextFlag" | "features">,
): boolean =>
	model.a2aContextFlag &&
	Option.exists(
		AsyncData.getData(model.features),
		(features) => features.a2aContextEnabled === true,
	);

const A2A_TAGS: ReadonlySet<PageTag> = new Set([
	"Tasks",
	"Task",
	"Knowledge",
	"Note",
	"Agents",
]);

/** Actor gating at view time (KTD7): the parser never sees the actor. */
export const routePageTag = (model: Model): PageTag => {
	const tag = model.route._tag;
	if (tag === "Rates" || (tag === "Credits" && !model.billingPreviewEnabled))
		return "NotFound";
	if ((tag === "Developer" || tag === "AI") && !isAdmin(model)) {
		return "NotFound";
	}
	if (tag === "Admin" && !isOwner(model)) return "NotFound";
	if (tag === "Integrations" && !model.integrationsEnabled) {
		return "NotFound";
	}
	if (A2A_TAGS.has(tag) && !a2aContextEnabled(model)) return "NotFound";
	return tag;
};

/** Which page Submodel should be alive: none while signed out. */
export const activePageTag = (model: Model): Option.Option<PageTag> => {
	if (model.auth._tag !== "SignedIn") {
		return Option.none();
	}
	return Option.some(routePageTag(model));
};

export const toShared = (model: Model): Option.Option<Shared> =>
	model.auth._tag === "SignedIn"
		? Option.some({
				generation: model.sessionGeneration,
				route: model.route,
				asOf: asOfOf(model.route),
				user: model.auth.user,
				balance: model.balance,
				features: model.features,
				tags: model.tags,
				attributeSchema: model.attributeSchema,
				integrationsEnabled: model.integrationsEnabled,
				billingPreviewEnabled: model.billingPreviewEnabled,
				a2aContextEnabled: a2aContextEnabled(model),
				theme: model.theme,
			})
		: Option.none();
